import { Box, IconButton } from '@mui/material';
import { useEffect, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EditProjectMemberModal from '../../projects/components/forms/edit-project-member-modal';
import DeleteProjectMemberModal from '../../projects/components/forms/delete-project-member-modal';
import { useQuery } from '@apollo/client';
import {
  GET_MEMBER,
  GET_PROJECT,
  GET_PROJECT_MEMBER_ALLOCATION
} from '../../projects/gql';
import { IProjectMembersDataTest } from '../type';
import { useAppSelector } from '../../../redux/hooks';

interface IProjectMemberRowProps {
  rowItem: IProjectMembersDataTest;
  updateProjectMemberCallback: () => Promise<void>;
  projectId: string;
  canEdit: boolean;
  canDelete: boolean;
  memberStatus: string;
}

const ProjectMemberAdminColumns = ({
  rowItem,
  updateProjectMemberCallback,
  projectId,
  memberStatus,
  canDelete
}: IProjectMemberRowProps): JSX.Element => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [displayMemberName, setDisplayMemberName] = useState('Loading');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editProjectMemberId, setEditProjectMemberId] = useState('');
  const [deleteProjectMemberId, setDeleteProjectMemberId] = useState('');
  const [loadingModal, setloadingModal] = useState(false);
  const [tentativeStartDate, setTentativeStartDate] = useState<Date>(
    new Date()
  );
  const [tentativeEndDate, setTentativeEndDate] = useState<Date>(new Date());
  const [isDeliveryResponsible, setIsDeliveryResponsible] = useState(false);
  const { profileData } = useAppSelector((state) => state.profileData);

  const { loading: projectMemberLoading, data: projectMemberData } = useQuery(
    GET_MEMBER,
    {
      variables: { id: editProjectMemberId }
    }
  );

  const { data: projectData } = useQuery(GET_PROJECT, {
    variables: { id: projectId }
  });

  const { refetch: refetchMemberAllocation } = useQuery(
    GET_PROJECT_MEMBER_ALLOCATION,
    {
      variables: { memberId: editProjectMemberId },
      skip: !editProjectMemberId
    }
  );

  useEffect(() => {
    if (projectMemberLoading) {
      setloadingModal(false);
    } else {
      setloadingModal(true);
    }
    if (projectMemberData) {
      setDisplayMemberName(projectMemberData.getMember.displayName);
    }
    // if (rowItem.memberRole.role === 'DELIVERY_RESPONSIBLE') {
    //   setIsDeliveryResponsible(true);
    // }
    rowItem.memberRoles.map(
      (role) =>
        role.projectRole.role === 'DELIVERY_RESPONSIBLE' &&
        setIsDeliveryResponsible(true)
    );
  }, [projectMemberData]);

  useEffect(() => {
    if (projectData) {
      setTentativeStartDate(projectData.getProject.tentativeStartDate);
      setTentativeEndDate(projectData.getProject.tentativeEndDate);
    }
  }, [projectData]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await refetchMemberAllocation();
    };
    if (editProjectMemberId !== '') {
      void fetchData();
    }
  }, [editProjectMemberId, refetchMemberAllocation]);

  return (
    <>
      {isDeliveryResponsible &&
      !profileData?.permissions?.project_manger.update ? (
        <Box
          sx={{
            fontSize: '14px',
            fontWeight: '600px',
            display: 'flex',
            flexDirection: 'row'
          }}
        >
          <IconButton>
            <EditOutlinedIcon sx={{ color: '#2C5DA766' }} />
          </IconButton>

          <IconButton>
            <DeleteOutlineIcon sx={{ color: '#BA1A1A66' }} />
          </IconButton>
        </Box>
      ) : (
        <Box
          sx={{
            fontSize: '14px',
            fontWeight: '600px',
            display: 'flex',
            flexDirection: 'row',
            gap: '2px'
          }}
        >
          <IconButton
            onClick={() => {
              setIsEditModalOpen(true);
              setEditProjectMemberId(rowItem.member.id);
            }}
          >
            <EditOutlinedIcon sx={{ color: '#2C5DA7' }} />
          </IconButton>

          <IconButton
            disabled={!canDelete}
            onClick={() => {
              setDeleteProjectMemberId(rowItem.id);
              setIsDeleteModalOpen(true);
            }}
          >
            <DeleteOutlineIcon
              sx={{ color: canDelete ? '#BA1A1A' : 'disabled' }}
            />
          </IconButton>
          {loadingModal && (
            <EditProjectMemberModal
              isVisible={isEditModalOpen}
              closeRequestForm={() => setIsEditModalOpen(false)}
              openRequestForm={() => setIsEditModalOpen(true)}
              projectMemberToEdit={{
                memberId: rowItem.member.id,
                projectMemberName: displayMemberName,
                projectMemberRole:
                  rowItem.memberRoles[0].projectRole.role ?? '',
                // projectMemberRole: rowItem.memberRole.role,
                fromDate: rowItem.roleStartDate,
                toDate: rowItem.roleEndDate,
                projectMemberAllocation: (rowItem.hoursPerWeek ?? 0).toString()
              }}
              projectId={projectId}
              memberStatus={memberStatus}
              updateProjectMemberCallback={updateProjectMemberCallback}
              tentativeStartDate={tentativeStartDate}
              tentativeEndDate={tentativeEndDate}
              projectMemberRoles={rowItem.memberRoles.map((role) => {
                return {
                  id: role.projectRole.id,
                  role: role.projectRole.role,
                  displayName: role.projectRole.displayName
                };
              })}
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
              projectMemberId={rowItem.id!}
            />
          )}
          <DeleteProjectMemberModal
            isVisible={isDeleteModalOpen}
            onCancel={() => setIsDeleteModalOpen(false)}
            onSuccess={() => setIsDeleteModalOpen(false)}
            memberId={deleteProjectMemberId}
            updateProjectMemberCallback={updateProjectMemberCallback}
          />
        </Box>
      )}
    </>
  );
};

export default ProjectMemberAdminColumns;
